import logger from "@airhelp/node-logger";
import { useState, useEffect } from "react";
import { Flex, Box, Text, Button } from "@chakra-ui/react";
import config from "config/config";
import { trackCustomEvent, trackEcommerceEvent } from "utils/tracking";
import { useTranslations } from "contexts/LocalizationContext/hooks";

import type { PlanBoxProps } from "modules/experiments/AhPlusPricingPlans/PlanBox/PlanBox.d";
import PlanBoxItem from "modules/experiments/AhPlusPricingPlans/PlanBox/PlanBoxItem";

const PlanBox = ({ planDetails, events, pushConversionEvent, ...props }: PlanBoxProps) => {
  const {
    planType,
    nameText,
    priceInfoText,
    pricePerMonth,
    items,
    buttonText,
    buttonType,
    buttonPath,
  } = planDetails;

  const [buttonHref, setButtonHref] = useState(`${config.ahplusUrl}${buttonPath}`);
  const translations = useTranslations();

  const handlerTrackEvents = () => {
    trackCustomEvent(events.gaEvent);

    if (events.ga4ETracking) {
      try {
        trackEcommerceEvent(events.ga4ETracking);
      } catch (error) {
        logger.error("Error parsing ga4ETrackingJSON", error);
      }
    }

    pushConversionEvent();
  }

  useEffect(() => {
    const planHref = new URL(buttonHref);
    const urlParams = new URLSearchParams(window.location.search);
    const urlSource = urlParams.get("source");

    if (!urlSource) return;

    planHref.searchParams.set("channel", `${urlSource}_${planType}`);

    setButtonHref(planHref.href);
  }, [buttonHref, planType]);

  return (
    <Flex bg="white" w="full" rounded="xl" direction="column" {...props}>
      <Box px={{ base: 6, lg: 16 }} mb={4} height="100%" maxWidth="394px" mx="auto">
        <Box mt={8} pb={4} mb={4} textAlign="center">
          <Text data-testid={`pricing-header-${planType}`} fontWeight="semibold" fontSize="3xl" m={0} p={0}>
            {nameText}
          </Text>
          <Box
            data-testid={`pricing-value-${planType}`}
            fontWeight="semibold"
            fontSize="6xl"
            color="primary.500"
          >
            <Text as="span" fontSize="6xl" fontWeight="semibold" color="primary.500" display="inline-block">
              {pricePerMonth}
            </Text>
            <Text as="span" fontSize="xl" ml="1" fontWeight="bold" color="greyscale.600" display="inline-block">
              /month
            </Text>
          </Box>
          <Text
            fontSize="xl"
            fontWeight="bold"
            color="greyscale.600"
            mt={-2}
            mb={5}
            dangerouslySetInnerHTML={{ __html: priceInfoText }}
          />
          <Text color="greyscale.600" fontSize="sm">
            {`${translations("plan_per_passenger")}, ${translations("plan_billed_annually")}`}
          </Text>
        </Box>
        <Box>
          {items.map((planItem) => (
            <PlanBoxItem key={planItem.text} items={planItem} />
          ))}
        </Box>
      </Box>
      <Box mb={12} px={{ base: 6, lg: 16 }} w="full">
        <Button
          variant={buttonType}
          data-testid={`pricing-choose-${planType}`}
          mb={pricePerMonth ? 4 : 9}
          w="full"
          as="a"
          href={buttonHref}
          title={translations(buttonText)}
          onClick={handlerTrackEvents}
        >
          {translations(buttonText)}
        </Button>
      </Box>
    </Flex>
  );
};

export default PlanBox;
